import React, { useEffect, useState } from 'react'
import { Typography, Form, Select, Input, Checkbox, Button } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import omit from 'lodash/omit'

import { actions } from '../create-company-page/actions'
import IntlMessages from '../../../util/IntlMessages'
import { filterOptions } from '../../../util/filter-options'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { sortCountriesWithPopularFirst } from '../../../util/sort-countries'
import { setCrispSessionParams } from '../helpers'

import { countries, ICountry } from '@vacationtracker/shared/data/countries'
import aboutUsSurvey from '@vacationtracker/shared/data/survey'

import SurveyFormPart from '../../../components/survey'
import { ICreateCompany, ICreateCompanyState } from '../create-company-page/types'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { track, trackConversionEvent } from '../../../services/analytics/analytics'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'

const { Title } = Typography
const { Option } = Select

interface ICreateCompanyProps {
  dispatch: Function
  state: ICreateCompanyState
  onFinishSetup: () => void
}

const CreateCompany = ({
  dispatch,
  state,
  onFinishSetup,
}: ICreateCompanyProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm()
  const history = useHistory()

  const [ selectedCountry, setSelectedCountry ] = useState<ICountry|undefined>()
  const [ agreeToTerms, setAgreeToTerms ] = useState(false)
  const [ platform, setPlatform ] = useState<PlatformEnum | undefined>()
  const [ selectedPlan, setSelectedPlan] = useState<SubscriptionPlanEnum>(SubscriptionPlanEnum.complete)

  const { createCompany, createUser } = state

  useEffect(() => {
    setAgreeToTerms(state.agreeToTerms)

    if (createCompany) {
      form.setFieldsValue({
        contactEmail: createCompany.contactEmail,
        name: createCompany.name,
        country: createCompany.country,
        state: createCompany?.state,
        agreeToTerms: state.agreeToTerms,
        surveyQuestionMainOption: createCompany.surveyQuestionMainOption,
        ...(createCompany?.state ? { state: createCompany.state } : {}),
        ...(createCompany.surveyQuestionSubOption ? { surveyQuestionSubOption: createCompany.surveyQuestionSubOption } : {}),
      })
      if (createCompany.country) {
        setSelectedCountry(countries.find(country => country.iso === createCompany.country))
      }
      if (createUser.platform && !platform) {
        setPlatform(createUser.platform)
      }
    }
  }, [createCompany])

  useEffect(() => {
    if (platform) {
      track('SIGNUP_COMPANY_CREATE_FORM_VIEWED', {
        platform,
        signupVariation: 'B_2025_06',
        plan: selectedPlan,
        email: createCompany.contactEmail || createUser.mail,
        timestamp: new Date().toISOString(),
        source: 'app',
        status: 'step1',
      })
      trackConversionEvent('SIGNUP_STARTED')
      setCrispSessionParams('step 1: company details')
    }
  }, [platform])

  useEffect(() => {
    const selectedPlanFromStorage = localStorage.getItem('vtSubscriptionPlan') as SubscriptionPlanEnum | null

    if (
      selectedPlanFromStorage
      && [SubscriptionPlanEnum.freemium, SubscriptionPlanEnum.core, SubscriptionPlanEnum.complete].includes(selectedPlanFromStorage)
    ) {
      setSelectedPlan(selectedPlanFromStorage)
    }
  }, [])

  const initialValues = {
    contactEmail: createCompany.contactEmail,
    name: createCompany.name,
    country: createCompany.country,
    state: createCompany.state,
    agreeToTerms: state.agreeToTerms,
  }

  const getAboutUsSurveyData = () => {
    const surveyOption = form.getFieldValue('surveyQuestionOption')
    const surveySubOption = form.getFieldValue('surveyQuestionSuboption')
    const surveyText = form.getFieldValue(`surveyQuestionSpecifyText-${surveyOption}`) || form.getFieldValue(`surveyQuestionSpecifyText-${surveySubOption}`)
    if (!surveyOption) {
      return ''
    }
    try {
      const option = aboutUsSurvey.questions[0].options?.find(option => option.id === surveyOption)
      const subOption = option?.subQuestion?.options?.find(option => option.id === surveySubOption)
      const data = {
        surveyQuestionMainOption: option?.name,
        ...((subOption?.name || surveyText) ? {surveyQuestionSubOption: subOption?.name || surveyText} : {}),
      }

      form.setFieldsValue(data)
      return data
    } catch (e) {
      return ''
    }
  }

  const handleCreateCompany = async () => {
    try {
      dispatch(actions.isCreateCompanyLoader(true))
      const formValues = await form.validateFields()
      const survey = getAboutUsSurveyData()
      const values = {...formValues, ...survey}

      localStorage.setItem('vtCreateCompany', JSON.stringify(omit(values, ['agreeToTerms', 'password', 'repeatPassword', 'userName']) as ICreateCompany))

      dispatch(actions.setCreateCompanyDetails(values))

      track('SIGNUP_COMPANY_CREATE_FORM_COMPLETED', {
        platform,
        signupVariation: 'B_2025_06',
        plan: selectedPlan || SubscriptionPlanEnum.complete,
        email: createCompany.contactEmail || createUser.mail,
      })

      onFinishSetup()
    } catch(error) {
      dispatch(actions.isCreateCompanyLoader(false))
      // logger.warning('ERROR HANDLE CREATE COMPANY', error)
    }
  }

  const onCountryChange = (countryId) => {
    track('SIGNUP_COMPANY_CREATE_FORM_LOCATION_SET', {
      platform,
      signupVariation: 'B_2025_06',
      country: countryId,
      plan: selectedPlan,
      email: createCompany.contactEmail,
    })
    setSelectedCountry(countries.find(country => country.iso === countryId))
    form.setFieldsValue({ state: '' })
  }

  const validation = () => {
    if (agreeToTerms) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(formatMessage({ id: 'createCompany.steps.companyForm.agreeToTerms.error' })))
  }

  return (
    <>
      <div className='auth-sidebar-form-back-buttonn'>
        <Button onClick={() => {history.goBack()}}><LeftOutlined /></Button>
      </div>
      {createUser?.name !== '' ?
        <Title level={3}><IntlMessages id='createCompany.IsGreatToMeetYouWithName' values={{ name: createUser.name }} /></Title> :
        <Title level={3}><IntlMessages id='createCompany.isGreatToMeetYou' /></Title>
      }
      <p className="switch-plan-anytime">
        <IntlMessages id="createCompany.switchPlanAnytime" values={{ plan: selectedPlan }} />
      </p>
      <Form
        disabled={state.isCreateCompanyLoader}
        form={form}
        layout="vertical"
        className="company-details-form"
        onFinish={() => {
          handleCreateCompany()
        }}
        initialValues={initialValues}
        requiredMark="optional"
      >
        <Form.Item
          label={<IntlMessages id="general.companyName" />}
          name="name"
          rules={[{
            required: true, message: <IntlMessages id="error.fieldIsRequired" />,
          }]}
        >
          <Input />
        </Form.Item>

        {createUser?.platform === PlatformEnum.Microsoft && createUser?.mail.includes('onmicrosoft.com') && (
          <Form.Item
            label={<IntlMessages id="createCompany.steps.companyForm.email" />}
            name="contactEmail"
            rules={[{
              required: true, message: <IntlMessages id="createCompany.steps.companyForm.contactEmail.validation.required" />,
            }, {
              type: 'email', message: <IntlMessages id="form.validEmailRequired" />,
            }]}
          >
            <Input type="email" />
          </Form.Item>
        )}

        <Form.Item
          label={<IntlMessages id="app.country" />}
          name="country"
          rules={[{ required: true, message: (<IntlMessages id="error.fieldIsRequired" />) }]}
        >
          <Select
            onChange={onCountryChange}
            filterOption={filterCountryOptions}
            showSearch
          >
            {sortCountriesWithPopularFirst(countries).map((country) => (
              <Option value={country.iso} key={country.iso}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedCountry && selectedCountry.states && selectedCountry.states.length > 0 && (
          <Form.Item
            label={<IntlMessages id="app.state" />}
            name="state"
            rules={[{ required: true, message: (<IntlMessages id="error.fieldIsRequired" />) }]}
          >
            <Select
              filterOption={filterOptions}
              showSearch
            >
              {selectedCountry.states
                .sort((a, b) => a.name < b.name ? -1 : 1)
                .map((state) => (
                  <Option value={state.iso} key={state.iso}>
                    {state.name}
                  </Option>
                ))
              }
            </Select>
          </Form.Item>
        )}

        <SurveyFormPart form={form} survey={aboutUsSurvey} />

        <Form.Item
          className='wrap-label'
          name="agreeToTerms"
          valuePropName="checked"
          rules={[{ validator: validation }]}
          wrapperCol={{ offset: 0, span: 24 }}
        >
          <Checkbox onChange={(e) => {
            setAgreeToTerms(e.target.checked)
          }}>
            <IntlMessages id="createCompany.agreeTerms"
              values={{
                // eslint-disable-next-line
                aTOS: (...chunks) => (
                  <a href="https://vacationtracker.io/terms-of-service/" target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
                // eslint-disable-next-line
                aPP: (...chunks) => (
                  <a href="https://vacationtracker.io/privacy-policy/" target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
                // eslint-disable-next-line
                aDPA: (...chunks) => (
                  <a href="https://vacationtracker.io/dpa/" target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
              }}
            />
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Button block loading={state.isCreateCompanyLoader} htmlType="submit" type="primary">
            {
              selectedPlan === SubscriptionPlanEnum.freemium
                ? <IntlMessages id="createCompany.startYourFreemiumSubscription" />
                : <IntlMessages id="createCompany.startYourFreeTrial" />
            }
          </Button>
        </Form.Item>
      </Form>
    </>
  )
}

export default CreateCompany